import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Check, Loader2, AlertCircle, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState("checking");

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setError("No session ID provided");
      setLoading(false);
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setPaymentStatus("paid");
        setBooking(response.data.booking);
        toast.success("Payment successful! Confirmation email sent.");
      } else if (response.data.status === "expired") {
        setPaymentStatus("expired");
        setError("Payment session expired. Please try booking again.");
      } else {
        // Keep checking
        setPaymentStatus("pending");
        setTimeout(checkPaymentStatus, 2000);
        return;
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      setError("Error verifying payment. Please contact us if you were charged.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="booking-loading">
        <Navigation />
        <div className="pt-32 pb-24">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto text-center">
              <Loader2 className="w-16 h-16 text-yellow-400 mx-auto mb-6 animate-spin" />
              <h1 className="heading-lg text-white mb-4">Processing Payment...</h1>
              <p className="text-body">Please wait while we confirm your payment.</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || paymentStatus === "expired") {
    return (
      <div className="min-h-screen bg-[#050505]" data-testid="booking-error">
        <Navigation />
        <div className="pt-32 pb-24">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 mx-auto mb-8 bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-10 h-10 text-red-400" />
              </div>
              <h1 className="heading-lg text-white mb-4">Payment Issue</h1>
              <p className="text-body mb-8">{error}</p>
              <button 
                onClick={() => navigate("/pricing")}
                className="btn-primary"
                data-testid="try-again-btn"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="booking-success">
      <Navigation />
      <div className="pt-32 pb-24">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-20 h-20 mx-auto mb-8 bg-green-500/20 flex items-center justify-center">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="heading-lg text-white mb-4">Booking Confirmed!</h1>
            <p className="text-body mb-2">
              Thank you for booking with Charlesworth Club.
            </p>
            <p className="text-body mb-8 flex items-center justify-center gap-2">
              <Mail className="w-4 h-4 text-yellow-400" />
              A confirmation email has been sent to <span className="text-yellow-400">{booking?.customer_email}</span>
            </p>
            
            {booking && (
              <div className="surface-card p-8 text-left mb-8" data-testid="booking-summary">
                <h3 className="heading-md text-white mb-6">Booking Summary</h3>
                <div className="space-y-4 text-neutral-300">
                  <div className="flex justify-between border-b border-white/10 pb-3">
                    <span>Booking Reference</span>
                    <span className="text-yellow-400 font-mono">{booking.id?.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-3">
                    <span>Package</span>
                    <span className="text-white">{booking.package_name}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-3">
                    <span>Event Date</span>
                    <span className="text-white">{formatDate(booking.event_date)}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-3">
                    <span>Event Type</span>
                    <span className="text-white capitalize">{booking.event_type}</span>
                  </div>
                  <div className="flex justify-between border-b border-white/10 pb-3">
                    <span>Venue</span>
                    <span className="text-white">{booking.venue_address}</span>
                  </div>
                  
                  {booking.selected_addons?.length > 0 && (
                    <div className="border-b border-white/10 pb-3">
                      <span className="text-neutral-400">Add-ons:</span>
                      <ul className="mt-2 space-y-1">
                        {booking.selected_addons.map((addon, idx) => (
                          <li key={idx} className="flex justify-between text-sm">
                            <span>{addon.label}</span>
                            <span className="text-yellow-400">+£{addon.price}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="flex justify-between border-b border-white/10 pb-3">
                    <span>Total Price</span>
                    <span className="text-white font-bold">£{booking.total_price?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Deposit Paid</span>
                    <span className="text-green-400 font-bold">£{booking.deposit_amount}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-white/10">
                    <span>Balance Due</span>
                    <span className="text-yellow-400 font-bold">£{(booking.total_price - booking.deposit_amount).toLocaleString()}</span>
                  </div>
                </div>
                
                <p className="text-neutral-500 text-sm mt-6">
                  Balance is due 2 weeks before your event
                </p>
              </div>
            )}

            <button 
              onClick={() => navigate("/")}
              className="btn-primary"
              data-testid="return-home"
            >
              Return to Home
            </button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BookingSuccess;
